import { useRouter } from 'next/router'
import Link from 'next/link';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { Tiles } from '@rebass/layout';
import { transparentize } from 'polished';
import { useTranslations } from 'next-intl';

import Banner from '@/components/banner';
import BrandGrid from '@/components/brand-grid';
import HowItWorks from '@/components/how-it-works';
import BrowseMore from '@/components/browse-more';
import Button from '@/components/button';
import SEO from '@/components/seo';
import { media } from '@/themes';
import { colorForCategory } from '@/utils/category';
import { generateDescription, flagForCountryCode, templateCurrency, parseIPAddress } from '@/utils';
import { fetchBrandsInCategoryAndCountry, lookupCountry } from '@/api';
import categories from '@data/cats.json';


const Select = dynamic(() => import('@/components/select'), { ssr: false });

const CountryPicker = styled(Select)`
  margin: 0 0 0 .5em !important;
  min-width: 15em;
`;

const CategoryName = styled.h1`
  color: ${props => props.color};
  margin: 0;
  margin-bottom: .75em;
`;

const Subtitle = styled.p`
  margin: 0 0 2em;
`;

const Container = styled.div`
  margin: 2em auto 5em;
  max-width: ${props => props.theme.maxContentWidth};
  padding: 0 1em;
`;

const LeftColumn = styled.div`
  ${media.sm`
    margin-bottom: 2em;
  `};
`;

const BannerBackground = styled.div`
  clip-path: ellipse(100% 100% at center top);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: -1;

  ${media.md`
    clip-path: ellipse(150% 100% at center top);
  `};

  ${media.sm`
    clip-path: ellipse(200% 100% at center top);
  `};

  ${({ color }) => css`
    background: white linear-gradient(to top left, ${transparentize(0.8, color)}, ${transparentize(0.9, color)});
  `};
`;

const BannerContent = styled(Tiles)`
  position: relative;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 2em;
`;

const CountrySelector = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 1.5em;

  ${media.sm`
    flex-direction: column;
  `};
`;

const Illustration = styled.img`
  max-height: 40em;
  max-width: 100%;
`;

const Index = ({ category, brands, currencies, countries, countryId }) => {
  const router = useRouter();
  const t = useTranslations('shop');
  const cT = useTranslations('categories');
  const title = category.title.replace(/\{CURRENCY_NAME\}/ig, generateDescription(currencies, t));
  const description = category.description !== '' ? templateCurrency(category.description, 'cryptocurrency', 'cryptocurrency') : null;
  const categoryColor = colorForCategory(category);

  const options = countries
    .map(c => ({
      value: c.id,
      label: `${c.id === 'XG' ? '🌎' : flagForCountryCode(c.id)} ${c.name}`,
      ...c
    }));

  let country = brands.length ? brands[0].country : countries.find(c => c.id === countryId);
  if (!country) {
    country = {
      id: 'US',
      name: 'United States',
      slug: 'united-states'
    }    
  }

  const selectedCountry = { value: country.id, label: `${country.id === 'XG' ? '🌎' : flagForCountryCode(country.id)} ${country.name}`, ...country };

  const onChangeCountry = country => {
    //TODO: This should go to /buy/gaming or whatever and show all products with filtered grid view
    router.push(`/buy/${country.slug}/${category.slug}`);
  }

  return <>
    <SEO
      title={t('seoTitle', { category: cT(category.slug || category.name) })}
      description={description}
    />
    <Banner background="transparent" style={{ padding: '3em 1.5em' }}>
      <BannerBackground color={categoryColor} />
      <BannerContent columns={[1, 1, 2]} textAlign={['center', 'center', 'left']}>
        <LeftColumn>
          <Illustration alt={`${category.name} illustration`} src={`/images/illustrations/categories/${category.slug}.png`} />
        </LeftColumn>
        <RightColumn>
          <CategoryName color={categoryColor}>{cT(category.slug || category.name)}</CategoryName>
          <Subtitle>{description}</Subtitle>
          <Link
            href={`/buy/${country.slug}/${category.slug}`}
            passHref
            title={`Browse ${category.name} gift cards to buy with Bitcoin, Ethereum, Litecoin, USDT`}>

            <Button primary>{t('browse')}</Button>

          </Link>
        </RightColumn>
      </BannerContent>
    </Banner>
    <Container>
      <p>
        <small>{title}</small>
      </p>
      {brands.length > 0 &&
        <CountrySelector>
          <h3>{t('popularProductsIn', { category: cT(category.slug || category.name) })}</h3>
          <CountryPicker
            aria-label={t('selectCountry')}
            isSearchable
            options={options}
            value={selectedCountry}
            onChange={onChangeCountry}
            placeholder={t('selectCountry')} />
        </CountrySelector>
      }
      <BrandGrid
        brands={brands}
        category={category}
        country={country}
        onReset={() => onChangeCountry(selectedCountry)}
        showEmptyState={!brands.length} />
    </Container>
    <BrowseMore />
    <HowItWorks />
  </>;
};

export async function getServerSideProps ({ params, query, req, locale }) {
  //TODO: Error handling and log to Bugsnag
  let ipAddress = query.ip || parseIPAddress(req);
  const countryId = await lookupCountry(ipAddress);
  const category = categories.find(c => c.slug === params.slug);
  const brands = await fetchBrandsInCategoryAndCountry({ countryId, categoryId: category.id, $limit: 12 });

  return {
    props: {
      countryId,
      category,
      brands,
      messages: require(`@/locales/${locale}.json`)
    }
  };
}

export default Index;
